/**
 * Deslizar uma folha para baixo para fechá-la, em qualquer folha do app.
 *
 * Funciona por cima das folhas existentes, sem mexer em cada uma: o dedo
 * arrasta a folha; ao soltar depois de um bom trecho (ou com um peteleco), ela
 * desce até sair e o app recebe o mesmo toque no fundo escuro que já a fecha.
 * Folhas que não podem fechar (ex.: a da chave de recuperação) ignoram esse
 * toque, e então a folha volta para o lugar.
 */

const SHEETS = [
  '.rk-sheet',
  '.am-card',
  '.pw',
  '.aic',
  '.lgs',
  '.cl-sheet',
  '.spv-sheet',
  '.ag-sheet',
  '.ct-sheet',
  '.cv-sheet',
  '.editsheet',
  '.lv-sheet',
].join(', ');
const BACKDROPS = [
  '.rk-backdrop',
  '.am-backdrop',
  '.pw-back',
  '.aic-back',
  '.lgs-back',
  '.cl-back',
  '.spv-back',
  '.ag-sheetwrap',
  '.ct-sheetwrap',
  '.cv-sheetwrap',
  '.editsheet-backdrop',
  '.lv-sheetwrap',
].join(', ');
const GRIPS = '.rk-handle, .am-grip, .ag-grip, .ct-grip, .cv-grip, .editsheet-handle, .lv-grip';
/** Faixa do topo da folha que sempre puxa (além da alça). */
const TOP_ZONE = 56;
const START_PX = 8;
const CLOSE_FRACTION = 0.28;
const FLICK_SPEED = 0.55; // px por ms

function sheetDuration(): number {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--sheet-dur');
  const seconds = Number.parseFloat(value);
  return Number.isFinite(seconds) ? seconds * 1000 : 460;
}

/** Algo dentro da folha que rola e ainda não está no topo: o gesto é dele. */
function scrolledInside(target: Element, sheet: Element): boolean {
  let node: Element | null = target;
  while (node && node !== sheet.parentElement) {
    if (node.scrollTop > 0) return true;
    node = node.parentElement;
  }
  return false;
}

function ownsGesture(target: Element): boolean {
  return !!target.closest(
    'input, textarea, select, [contenteditable="true"], [data-no-sheet-drag], input[type="range"]',
  );
}

export function installSheetDrag(): () => void {
  let sheet: HTMLElement | null = null;
  let backdrop: HTMLElement | null = null;
  let startY = 0;
  let startX = 0;
  let lastY = 0;
  let lastT = 0;
  let speed = 0;
  let dragging = false;
  let decided = false;

  const reset = () => {
    sheet = null;
    backdrop = null;
    dragging = false;
    decided = false;
  };

  const onStart = (event: TouchEvent) => {
    if (event.touches.length !== 1) return;
    const target = event.target instanceof Element ? event.target : null;
    const found = target?.closest<HTMLElement>(SHEETS) ?? null;
    if (!target || !found || found.classList.contains('sheet-settling')) return;
    const touch = event.touches[0]!;
    const fromTop = touch.clientY - found.getBoundingClientRect().top;
    const onGrip = !!target.closest(GRIPS) || fromTop <= TOP_ZONE;
    if (!onGrip && (ownsGesture(target) || scrolledInside(target, found))) return;
    sheet = found;
    backdrop = found.closest<HTMLElement>(BACKDROPS) ?? found.parentElement;
    startY = lastY = touch.clientY;
    startX = touch.clientX;
    lastT = event.timeStamp;
    speed = 0;
    dragging = false;
    decided = false;
  };

  const onMove = (event: TouchEvent) => {
    if (!sheet) return;
    const touch = event.touches[0]!;
    const dy = touch.clientY - startY;
    const dx = touch.clientX - startX;
    if (!decided) {
      if (Math.abs(dy) < START_PX && Math.abs(dx) < START_PX) return;
      decided = true;
      // Só puxa para baixo e na vertical; o resto segue com a rolagem normal.
      if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
        reset();
        return;
      }
      dragging = true;
      sheet.classList.add('sheet-dragging');
    }
    if (!dragging) return;
    event.preventDefault();
    const dt = Math.max(1, event.timeStamp - lastT);
    speed = (touch.clientY - lastY) / dt;
    lastY = touch.clientY;
    lastT = event.timeStamp;
    const offset = Math.max(0, dy);
    sheet.style.transform = `translateY(${offset}px)`;
    if (backdrop && backdrop !== sheet) {
      const fade = 1 - Math.min(1, offset / Math.max(1, sheet.offsetHeight));
      backdrop.style.opacity = String(0.35 + fade * 0.65);
    }
  };

  const settle = (node: HTMLElement, shade: HTMLElement | null, transform: string) => {
    node.classList.remove('sheet-dragging');
    node.classList.add('sheet-settling');
    // Força o navegador a aplicar a transição a partir da posição atual.
    void node.offsetHeight;
    node.style.transform = transform;
    if (shade && shade !== node) {
      shade.style.transition = `opacity ${sheetDuration()}ms ease`;
      shade.style.opacity = transform === '' ? '' : '0';
    }
  };

  const onEnd = () => {
    const node = sheet;
    const shade = backdrop;
    const wasDragging = dragging;
    reset();
    if (!node || !wasDragging) return;
    const offset = Number.parseFloat(node.style.transform.replace(/[^\d.-]/g, '')) || 0;
    const close = offset > node.offsetHeight * CLOSE_FRACTION || speed > FLICK_SPEED;
    const duration = sheetDuration();
    if (!close) {
      settle(node, shade, '');
      window.setTimeout(() => {
        node.classList.remove('sheet-settling');
        if (shade && shade !== node) shade.style.transition = '';
      }, duration);
      return;
    }
    settle(node, shade, 'translateY(110%)');
    window.setTimeout(() => {
      // O mesmo toque no fundo que as folhas já entendem como "fechar".
      if (shade && shade !== node) shade.click();
      window.setTimeout(() => {
        if (!node.isConnected) return;
        // Esta folha não fecha assim: volta para o lugar.
        settle(node, shade, '');
        window.setTimeout(() => {
          node.classList.remove('sheet-settling');
          if (shade && shade !== node) {
            shade.style.transition = '';
            shade.style.opacity = '';
          }
        }, duration);
      }, 80);
    }, duration);
  };

  document.addEventListener('touchstart', onStart, { passive: true });
  document.addEventListener('touchmove', onMove, { passive: false });
  document.addEventListener('touchend', onEnd);
  document.addEventListener('touchcancel', onEnd);
  return () => {
    document.removeEventListener('touchstart', onStart);
    document.removeEventListener('touchmove', onMove);
    document.removeEventListener('touchend', onEnd);
    document.removeEventListener('touchcancel', onEnd);
  };
}
